/**
 * Region detection for the template editor: AI detect, with default regions as
 * a fallback. Shared by the "By region" toggle and the top-bar Detect Regions
 * button, which carried two copies of this flow.
 *
 * Pure — the API calls are injected — so the fallback decision is testable
 * under CRA's jest without rendering the editor.
 *
 * THE FALLBACK MUST NOT FIRE ON A MISSING SAMPLE. It used to run on any 400 or
 * 404 from detect-regions, and a deleted sample document surfaced as a 400. The
 * fallback then replaced the template's authored regions with four
 * placeholders. detect-regions now answers a missing sample with its own code,
 * and that code stops the flow here with the server's message.
 *
 * The server independently refuses to replace existing regions without an
 * explicit { replace: true }, which this flow never sends — so even a fallback
 * that does run cannot delete authored regions; its refusal is shown instead.
 */

export const SAMPLE_DOCUMENT_MISSING = 'sample_document_missing';

export function isSampleDocumentMissing(err) {
  return err?.response?.data?.code === SAMPLE_DOCUMENT_MISSING;
}

/** Whether a detect-regions failure should fall back to default regions. */
export function shouldFallBackToDefaultRegions(err) {
  if (isSampleDocumentMissing(err)) return false;
  const status = err?.response?.status;
  return status === 400 || status === 404;
}

/**
 * Run detection (or default regions when the template has no sample).
 * Resolves { ok: true, fresh } with the refetched template, or
 * { ok: false, error } with a message to show. Never throws.
 */
export async function detectOrDefaultRegions({ templateId, hasSourceDocument, api }) {
  try {
    if (hasSourceDocument) {
      await api.detectRegions(templateId);
    } else {
      await api.createDefaultRegions(templateId);
    }
    let fresh = await api.getTemplateFull(templateId);
    // AI detect ran but produced no regions — nothing to replace, so defaults are safe.
    if (fresh.ok && (fresh.regions || []).length === 0) {
      await api.createDefaultRegions(templateId);
      fresh = await api.getTemplateFull(templateId);
    }
    return { ok: true, fresh };
  } catch (err) {
    if (!shouldFallBackToDefaultRegions(err)) {
      return { ok: false, error: err?.response?.data?.error || err?.message || 'Failed to detect regions' };
    }
    try {
      await api.createDefaultRegions(templateId);
      const fresh = await api.getTemplateFull(templateId);
      return { ok: true, fresh };
    } catch (fallbackErr) {
      return { ok: false, error: fallbackErr?.response?.data?.error || fallbackErr?.message || 'Failed to create regions' };
    }
  }
}
